import React, { useState, useEffect } from 'react'
import { Classes } from '@blueprintjs/core'
import { NavigateFn } from '@reach/router'
import {
  IconButton,
  MiniManagementSection,
  IGridTableCellProps,
  Avatar,
  IGridTableCell,
  SpaceCard,
  SpaceCardSkeleton,
  EmployeeWithDetailsPopover,
  ColorSelect,
  Toaster,
  PrettyDate,
  Dialog,
} from '@ui'
import api, { IDMTableDepartment, IEmployee, IDMDialogSpace, IEmployeeSpaceAssignment } from '@api'
import { useShouldReload, useReload } from '@hooks/reloadContext'
import { ApolloError } from 'apollo-boost'
import classnames from 'classnames'
import track from '@track'
import Event from '@track/Event'
import { Tooltip2 } from '@blueprintjs/popover2'
import css from '../EmployeeManagement/employeeManagement.module.scss'
import { useFloor, useMapInit } from '@providers/mapProvider'
interface DepartmentDetailsDialogProps {
  departmentId: string
  isAdminSearch?: string
  navigate: NavigateFn
}

let oldColor = '#000000'

export default function DepartmentDetailsDialog({
  departmentId,
  isAdminSearch,
  navigate,
}: DepartmentDetailsDialogProps) {
  const floor = useFloor()
  const map = useMapInit()
  const [dept, setDept] = useState<IDMTableDepartment | null>(null)

  const shouldReload = useShouldReload()
  const reload = useReload()
  const [spaceId, setSpaceId] = useState('')
  useEffect(() => {
    api.department.details({ id: departmentId }).then((dept) => {
      setDept(dept)
    })
  }, [departmentId, shouldReload.departments])

  useEffect(() => {
    if (map.current) {
      floor.map && zoomToSpace(spaceId)
    }
  }, [floor.map, spaceId])

  const zoomToSpace = async (spaceId: string) => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    map?.current?.zoomTo(await api.space.getSpaceCoordinates(spaceId))
    floor.selection?.add([spaceId])
    navigate?.(window.location.pathname)
  }
  const navigateToSpace = (spaceAssignment: IEmployeeSpaceAssignment) => {
    const isAnotherFloor = floor?.floorId !== spaceAssignment?.space?.floor?.id
    setSpaceId(spaceAssignment?.space?.id)
    if (isAnotherFloor) {
      floor?.setFloorId?.(spaceAssignment?.space?.floor?.id)
    } else {
      zoomToSpace(spaceAssignment?.space?.id)
    }
  }
  return (
    <Dialog
      isOpen
      usePortal
      style={{ width: 900, position: 'relative' }}
      canEscapeKeyClose={false}
      transitionDuration={200}
      isCloseButtonShown={false}
      onClose={() => navigate?.(window.location.pathname)}
      toolbar={
        <div className="dialog-buttons">
          <IconButton
            minimal
            tooltip="Close"
            iconName="times"
            onClick={() => navigate?.(window.location.pathname)}
          />
        </div>
      }
      body={
        <div className={Classes.DIALOG_BODY}>
          {dept?.colorUpdatedAt && dept.updatedBy && (
            <div className="mb-6 flex items-center">
              <Avatar
                size={Avatar.Size.XS}
                name={dept.updatedBy.employee.name}
                assets={dept.updatedBy.employee.assets}
              />
              <EmployeeWithDetailsPopover
                buttonProps={{ minimal: true, small: true }}
                employee={dept.updatedBy?.employee}
              />
              <span className="text-grey-600">
                {' '}
                updated on <PrettyDate value={dept.colorUpdatedAt} />
              </span>
            </div>
          )}

          <div className="flex items-center">
            <ColorSelect
              buttonProps={{
                small: true,
                minimal: true,
              }}
              value={dept?.color}
              onChange={(color: string) => {
                // synchronous saving of old color for undo
                if (dept) oldColor = dept.color

                return api.department
                  .updateMany({
                    ids: [departmentId],
                    changes: { color },
                  })
                  .then(() => {
                    Toaster.success({
                      message: 'Color updated',
                      action: {
                        text: 'Undo',
                        onClick: () => {
                          track.departmentDialog({
                            eventName: Event.UNDO_CLICK,
                            props: {
                              component: 'Color picker',
                              actionName: 'Undo Color Change',
                            },
                          })
                          api.department
                            .updateMany({
                              ids: [departmentId],
                              changes: { color: oldColor },
                            })
                            .then(() => reload.departments())
                        },
                      },
                    })
                    reload.departments()
                  })
                  .catch((error: ApolloError) => {
                    Toaster.fail({ error })
                  })
              }}
            />
            <div
              className={classnames('ml-1 text-2xl', {
                [Classes.SKELETON]: !dept,
              })}>
              <span>{dept?.name || 'placeholder'}</span>
            </div>
          </div>
          <div className="mt-8">
            <MiniManagementSection<IEmployee>
              trackProps={{
                track: track.departmentDialog,
                component: 'Employee Manage',
              }}
              title="Employees"
              list={(variables) =>
                api.department.employee.list({
                  departmentId,
                  ...variables,
                })
              }
              gridTableProps={{
                header: true,
                sticky: false,
                columns: [
                  {
                    label: 'Employee',
                    Cell: ({ skeleton, cell }: IGridTableCellProps<IEmployee>) => {
                      if (skeleton) {
                        return (
                          <div className="flex flex-grow items-center">
                            <Avatar size={Avatar.Size.XS} skeleton />
                            <div className="ml-2">
                              <div className={`inline-block text-sm ${Classes.SKELETON}`}>
                                placeholder
                              </div>
                              <div className={`text-xs mt-1 ${Classes.SKELETON}`}>
                                email placeholder
                              </div>
                            </div>
                          </div>
                        )
                      }

                      const employee = (cell as IGridTableCell<IEmployee>).data

                      return (
                        <div className="flex items-center">
                          <Avatar
                            size={Avatar.Size.XS}
                            name={employee.name}
                            assets={employee.assets}
                          />
                          <div className="ml-2">
                            <div className="text-sm">{employee.name}</div>
                            <div className="text-xs  text-grey-600">{employee.email}</div>
                            {!!isAdminSearch && (
                              <span
                                className={css.space_link}
                                onClick={(e) => {
                                  e.stopPropagation()
                                  employee?.spaceAssignment &&
                                    navigateToSpace(employee?.spaceAssignment)
                                }}>
                                <Tooltip2 content={'Locate Seat'} position="right">
                                  {employee?.spaceAssignment?.space?.externalId}
                                </Tooltip2>
                              </span>
                            )}
                          </div>
                        </div>
                      )
                    },
                  },
                  {
                    label: 'Department / Team',
                    Cell: ({ skeleton, cell }: IGridTableCellProps<IEmployee>) => {
                      if (skeleton) {
                        return (
                          <div className="text-sm">
                            <span className={Classes.SKELETON}>Department name</span>
                          </div>
                        )
                      }

                      const employee = (cell as IGridTableCell<IEmployee>).data

                      return (
                        <div className="w-full truncate text-sm">{`${employee.dept?.name}${
                          employee.team ? ' | ' + employee.team.name : ''
                        }`}</div>
                      )
                    },
                  },
                ],
              }}
            />
          </div>
          {!isAdminSearch && (
            <div className="mt-8">
              <MiniManagementSection<IDMDialogSpace>
                trackProps={{
                  track: track.departmentDialog,
                  component: 'Space Manage',
                }}
                title="Spaces"
                list={(variables) =>
                  api.department.space.list({
                    departmentId,
                    ...variables,
                  })
                }
                gridTableProps={{
                  noDataMessage: 'No spaces assigned yet',
                  sticky: false,
                  columns: [
                    {
                      label: 'Space',
                      Cell: ({ skeleton, cell }: IGridTableCellProps<IDMDialogSpace>) => {
                        return skeleton ? (
                          <SpaceCardSkeleton />
                        ) : (
                          <SpaceCard space={(cell as IGridTableCell<IDMDialogSpace>).data} />
                        )
                      },
                    },
                    {
                      label: 'Department Allocation',
                      Cell: ({ skeleton, cell }: IGridTableCellProps<IDMDialogSpace>) => {
                        if (skeleton) {
                          return (
                            <div className="text-sm">
                              <span className={Classes.SKELETON}>Department name</span>
                            </div>
                          )
                        }

                        const space = (cell as IGridTableCell<IDMDialogSpace>).data
                        const dept = space?.spaceAssignment?.department
                        const team = space?.spaceAssignment?.team

                        return dept ? (
                          <div className="w-full truncate text-sm">{`${dept.name}${
                            team ? ' | ' + team.name : ''
                          }`}</div>
                        ) : (
                          <span>-</span>
                        )
                      },
                    },
                  ],
                }}
              />
            </div>
          )}
        </div>
      }
    />
  )
}
